import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toast } from 'sonner';

type Mode = 'single' | 'per_user' | 'global';

interface ModeConfig {
  label: string;
  description: string;
  scope: 'per_user' | 'global';
  maxRedemptions: number | null;
  usageLabel: string;
}

const MODES: Record<Mode, ModeConfig> = {
  single: {
    label: 'Einzelcode',
    description: 'Nur ein Nutzer kann diesen Code einlösen — gut für einzelne Tester.',
    scope: 'per_user',
    maxRedemptions: 1,
    usageLabel: 'Nachrichten für den Einlöser',
  },
  per_user: {
    label: 'Pro-Nutzer-Budget',
    description: 'Beliebig viele Nutzer können den Code einlösen. Jeder bekommt sein eigenes Kontingent.',
    scope: 'per_user',
    maxRedemptions: null,
    usageLabel: 'Nachrichten pro Einlöser',
  },
  global: {
    label: 'Gemeinsames Budget',
    description: 'Beliebig viele Nutzer teilen sich ein Gesamtkontingent — gut für Promo-Aktionen.',
    scope: 'global',
    maxRedemptions: null,
    usageLabel: 'Gesamtkontingent (geteilt)',
  },
};

export function VoucherCreate() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('single');
  const [code, setCode] = useState('');
  const [usageLimit, setUsageLimit] = useState('50');
  const [campaignTag, setCampaignTag] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [isStarter, setIsStarter] = useState(false);
  const [loading, setLoading] = useState(false);

  const config = MODES[mode];

  useEffect(() => {
    if (mode !== 'per_user') setIsStarter(false);
  }, [mode]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const limit = parseInt(usageLimit, 10);
    if (!Number.isInteger(limit) || limit <= 0) {
      toast.error('Nutzungslimit muss eine positive Zahl sein');
      return;
    }
    setLoading(true);
    try {
      const created = await api.operator.vouchers.create({
        code: code.trim() || undefined,
        usageLimit: limit,
        scope: config.scope,
        maxRedemptions: config.maxRedemptions,
        isStarter,
        expiresAt: expiresAt || null,
        campaignTag: campaignTag.trim() || null,
      });
      toast.success(`Gutschein ${created.code} angelegt`);
      navigate(routes.coach.operator.vouchers());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AdminShell pageTitle="Voucher anlegen">
      <PageHeader title="Gutschein anlegen" showBack />
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-6 space-y-5">

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium mb-2">Modus</legend>
          {(Object.keys(MODES) as Mode[]).map((m) => (
            <label key={m} className={`block rounded-xl border px-4 py-3 cursor-pointer transition-colors ${mode === m ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'}`}>
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="mode"
                  value={m}
                  checked={mode === m}
                  onChange={() => setMode(m)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">{MODES[m].label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{MODES[m].description}</p>
                </div>
              </div>
            </label>
          ))}
        </fieldset>

        {mode === 'per_user' && (
          <label className={`block rounded-xl border px-4 py-3 cursor-pointer transition-colors ${isStarter ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'}`}>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={isStarter}
                onChange={(e) => setIsStarter(e.target.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <p className="text-sm font-medium">Als Starter-Voucher verwenden</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Neue Nutzer bekommen dieses Kontingent beim ersten Login automatisch. Ein vorhandener Starter wird dabei ersetzt.
                </p>
              </div>
            </div>
          </label>
        )}

        <label className="block space-y-1">
          <span className="text-sm font-medium">Code (optional)</span>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Leer lassen für Zufallscode"
            className="font-mono"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm font-medium">{config.usageLabel}</span>
          <Input
            type="number"
            min="1"
            value={usageLimit}
            onChange={(e) => setUsageLimit(e.target.value)}
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm font-medium">Kampagne (optional)</span>
          <Input
            value={campaignTag}
            onChange={(e) => setCampaignTag(e.target.value)}
            placeholder="z.B. beta-wave-1"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm font-medium">Ablaufdatum (optional)</span>
          <Input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </label>

        <div className="flex gap-2">
          <Button type="submit" disabled={loading}>Anlegen</Button>
          <Button type="button" variant="outline" onClick={() => navigate(routes.coach.operator.vouchers())}>
            Abbrechen
          </Button>
        </div>
      </form>
    </AdminShell>
  );
}
